import { useMemo } from 'react';
import { Anchor, Center, Container, Divider, Group, Loader, Stack, Tabs, Text, Title } from '@mantine/core';
import { IconDatabase, IconPhone, IconPuzzle, IconSettings, IconToggleRight, IconUsers } from '@tabler/icons-react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useSubscription } from 'urql';
import { isUuid } from '@shared';
import { ErrorAlert } from '@/components/error-alert';
import { getErrorMessage } from '@/lib/error-message';
import { ApiAccessPanel } from './-components/api-access-panel';
import { CrmPanel } from './-components/crm-panel';
import { DeleteOrganizationPanel } from './-components/delete-organization-panel';
import { FeaturesPanel } from './-components/features-panel';
import { MembersPanel } from './-components/members-panel';
import { OrganizationDetailsForm } from './-components/organization-details-form';
import { PhoneNumbersPanel } from './-components/phone-numbers-panel';
import { WidgetsPanel } from './-components/widgets-panel';
import { SystemOrganizationSubscription, SystemOrganizationWidgetsSubscription } from './-components/graphql';
import type { SystemOrganization } from './-components/graphql';

export const Route = createFileRoute('/_authed/system/organizations/$id')({
  component: SystemOrganizationPage,
});

function OrganizationTabs({ organization }: { organization: SystemOrganization }) {
  const [{ data: widgetData }] = useSubscription({
    query: SystemOrganizationWidgetsSubscription,
    variables: { organizationId: organization.id },
  });

  const enabledWidgetIds = useMemo(
    () => new Set((widgetData?.organizationWidgets ?? []).filter((w) => w.enabled).map((w) => w.widgetId)),
    [widgetData],
  );

  return (
    <Tabs defaultValue="general">
      <Tabs.List>
        <Tabs.Tab value="general" leftSection={<IconSettings size={14} />}>
          General
        </Tabs.Tab>
        <Tabs.Tab value="crm" leftSection={<IconDatabase size={14} />}>
          CRM Integration
        </Tabs.Tab>
        <Tabs.Tab value="members" leftSection={<IconUsers size={14} />}>
          Members ({organization.membersAggregate.aggregate?.count ?? 0})
        </Tabs.Tab>
        <Tabs.Tab value="phones" leftSection={<IconPhone size={14} />}>
          Phone Numbers ({organization.phoneNumbersAggregate.aggregate?.count ?? 0})
        </Tabs.Tab>
        <Tabs.Tab value="features" leftSection={<IconToggleRight size={14} />}>
          Features
        </Tabs.Tab>
        <Tabs.Tab value="widgets" leftSection={<IconPuzzle size={14} />}>
          Widgets
        </Tabs.Tab>
      </Tabs.List>

      <Tabs.Panel value="general" pt="lg">
        <Stack gap="lg">
          <OrganizationDetailsForm organization={organization} />
          <Divider />
          <ApiAccessPanel organizationId={organization.id} apiKey={organization.apiKey} />
          <Divider />
          <DeleteOrganizationPanel organizationId={organization.id} organizationName={organization.name} />
        </Stack>
      </Tabs.Panel>

      <Tabs.Panel value="crm" pt="lg">
        <CrmPanel organization={organization} />
      </Tabs.Panel>

      <Tabs.Panel value="members" pt="lg">
        <MembersPanel organizationId={organization.id} />
      </Tabs.Panel>

      <Tabs.Panel value="phones" pt="lg">
        <PhoneNumbersPanel organizationId={organization.id} />
      </Tabs.Panel>

      <Tabs.Panel value="features" pt="lg">
        <FeaturesPanel organizationId={organization.id} enabledWidgetIds={enabledWidgetIds} />
      </Tabs.Panel>

      <Tabs.Panel value="widgets" pt="lg">
        <WidgetsPanel organizationId={organization.id} enabledWidgetIds={enabledWidgetIds} />
      </Tabs.Panel>
    </Tabs>
  );
}

function SystemOrganizationPage() {
  const { id } = Route.useParams();
  const hasValidId = isUuid(id);

  const [{ data, error }] = useSubscription({
    query: SystemOrganizationSubscription,
    variables: { id },
    pause: !hasValidId,
  });

  if (error) {
    return (
      <Container size="xl" py="xl">
        <ErrorAlert message={getErrorMessage(error, 'Unable to load this organization')} />
      </Container>
    );
  }

  if (hasValidId && !data) {
    return (
      <Center h="50vh">
        <Loader size="lg" />
      </Center>
    );
  }

  if (!data?.organizationsByPk) {
    return (
      <Container size="xl" py="xl">
        <ErrorAlert message="Organization not found" />
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        <Group gap="xs">
          <Anchor component={Link} to="/system/organizations" size="sm" c="dimmed" td="none">
            Organizations
          </Anchor>
          <Text size="sm" c="dimmed">
            /
          </Text>
          <Text size="sm" fw={500}>
            {data.organizationsByPk.name}
          </Text>
        </Group>

        <Title order={2}>{data.organizationsByPk.name}</Title>

        <OrganizationTabs organization={data.organizationsByPk} />
      </Stack>
    </Container>
  );
}
